import React, { useState, useMemo } from 'react';
import {
  Key,
  Laptop,
  Globe,
  Layers,
  Database,
  TrendingUp,
  AlertTriangle,
  Package,
  Shield,
  CheckCircle
} from 'lucide-react';
import MaturityGauge from './components/MaturityGauge';
import ScoreBreakdownPanel from './components/ScoreBreakdownPanel';
import { CISA_PILLARS } from '../config/cisaMaturityModel';

/**
 * CisaDashboardHeader - Overall score gauge + pillar cards + quick stats + three-layer breakdown
 *
 * Props:
 * - summary: { overallScore, pillarScores, functionsWithGaps, totalProductsAdopted, ... }
 * - derivedScores: { [functionCode]: DerivedScoreObject }
 * - onSelectPillar: (pillarCode) => void
 * - configIncompleteCount: number (optional)
 */
const CisaDashboardHeader = ({
  summary = {},
  derivedScores = {},
  onSelectPillar,
  configIncompleteCount = 0
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const {
    overallScore = 1.0,
    pillarScores = {},
    functionsWithGaps = 0,
    totalProductsAdopted = 0
  } = summary;

  // Calculate aggregate confidence metrics
  const confidenceMetrics = useMemo(() => {
    const scores = Object.values(derivedScores);
    if (scores.length === 0) {
      return { avgConfidence: 0, overrideCount: 0, expiredCount: 0 };
    }

    let totalEvidence = 0;
    let overrideCount = 0;
    let expiredCount = 0;

    for (const score of scores) {
      totalEvidence += score.evidenceConfidence || 0;
      if (score.hasOverride) overrideCount++;
      expiredCount += score.expiredEvidenceCount || 0;
    }

    return {
      avgConfidence: Math.round(totalEvidence / scores.length),
      overrideCount,
      expiredCount
    };
  }, [derivedScores]);

  // Get pillar icon component
  const getPillarIcon = (code) => {
    const icons = {
      IDENTITY: Key,
      DEVICES: Laptop,
      NETWORKS: Globe,
      APPLICATIONS: Layers,
      DATA: Database
    };
    return icons[code] || Key;
  };

  // Get maturity level from score
  const getLevelFromScore = (score) => {
    if (score >= 3.5) return 'Optimal';
    if (score >= 2.5) return 'Advanced';
    if (score >= 1.5) return 'Initial';
    return 'Traditional';
  };

  // Get level class for styling
  const getLevelClass = (score) => {
    if (score >= 3.5) return 'optimal';
    if (score >= 2.5) return 'advanced';
    if (score >= 1.5) return 'initial';
    return 'traditional';
  };

  // Get confidence indicator color
  const getConfidenceColor = (confidence) => {
    if (confidence >= 85) return '#10b981';
    if (confidence >= 65) return '#3b82f6';
    if (confidence >= 40) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="cisa-dashboard-header">
      {/* Main score section */}
      <div className="cisa-dashboard-header-main">
        <div className="cisa-dashboard-header-score">
          <MaturityGauge score={overallScore} size="large" showLabel={true} />
          <div className="cisa-dashboard-header-score-details">
            <h2 className="cisa-dashboard-header-title">Overall Maturity Score</h2>
            <p className="cisa-dashboard-header-subtitle">
              Across {CISA_PILLARS.length} CISA Zero Trust pillars
            </p>
            {/* Evidence confidence indicator */}
            {Object.keys(derivedScores).length > 0 && (
              <div className="cisa-dashboard-confidence-indicator">
                <Shield size={14} style={{ color: getConfidenceColor(confidenceMetrics.avgConfidence) }} />
                <span
                  className="cisa-dashboard-confidence-value"
                  style={{ color: getConfidenceColor(confidenceMetrics.avgConfidence) }}
                >
                  {confidenceMetrics.avgConfidence}%
                </span>
                <span className="cisa-dashboard-confidence-label">Evidence Confidence</span>
                {confidenceMetrics.overrideCount > 0 && (
                  <span className="cisa-dashboard-override-badge" title={`${confidenceMetrics.overrideCount} manual overrides`}>
                    <AlertTriangle size={12} />
                    {confidenceMetrics.overrideCount}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pillar progress bars */}
        <div className="cisa-dashboard-header-pillars">
          <h3 className="cisa-dashboard-header-section-title">Maturity by Pillar</h3>
          <div className="cisa-dashboard-header-pillar-bars">
            {CISA_PILLARS.map(pillar => {
              const score = pillarScores[pillar.code] || 1.0;
              const percentage = ((score - 1) / 3) * 100;
              const IconComponent = getPillarIcon(pillar.code);

              return (
                <button
                  key={pillar.code}
                  type="button"
                  className="cisa-dashboard-pillar-bar-item"
                  onClick={() => onSelectPillar?.(pillar.code)}
                >
                  <div className="cisa-dashboard-pillar-bar-header">
                    <div className="cisa-dashboard-pillar-bar-label">
                      <IconComponent size={14} style={{ color: pillar.color }} />
                      <span>{pillar.name}</span>
                    </div>
                    <span className="cisa-dashboard-pillar-bar-score">{score.toFixed(1)}</span>
                  </div>
                  <div className="cisa-dashboard-pillar-bar-track">
                    <div
                      className="cisa-dashboard-pillar-bar-fill"
                      style={{ width: `${percentage}%`, background: pillar.color }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Three-layer scoring breakdown panel */}
      {Object.keys(derivedScores).length > 0 && (
        <ScoreBreakdownPanel
          derivedScores={derivedScores}
          overallScore={overallScore}
          pillarScores={pillarScores}
          isExpanded={showBreakdown}
          onToggle={() => setShowBreakdown(!showBreakdown)}
        />
      )}

      {/* Pillar cards */}
      <div className="cisa-dashboard-header-cards">
        {CISA_PILLARS.map(pillar => {
          const score = pillarScores[pillar.code] || 1.0;
          const level = getLevelFromScore(score);
          const levelClass = getLevelClass(score);
          const IconComponent = getPillarIcon(pillar.code);

          return (
            <button
              key={pillar.code}
              type="button"
              className="cisa-dashboard-pillar-card"
              onClick={() => onSelectPillar?.(pillar.code)}
              style={{ '--pillar-color': pillar.color }}
            >
              <div className="cisa-dashboard-pillar-card-header">
                <div
                  className="cisa-dashboard-pillar-card-icon"
                  style={{ background: pillar.color }}
                >
                  <IconComponent size={16} />
                </div>
                <span className="cisa-dashboard-pillar-card-name">{pillar.name}</span>
              </div>
              <div className="cisa-dashboard-pillar-card-score">
                <span className="cisa-dashboard-pillar-card-score-value">{score.toFixed(1)}</span>
                <span className={`cisa-dashboard-pillar-card-level cisa-level--${levelClass}`}>
                  {level}
                </span>
              </div>
              <div className="cisa-dashboard-pillar-card-meta">
                <span>Click to assess</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick stats row */}
      <div className="cisa-dashboard-header-stats">
        {functionsWithGaps > 0 && (
          <div className="cisa-dashboard-stat cisa-dashboard-stat--warning">
            <AlertTriangle size={16} />
            <span className="cisa-dashboard-stat-value">{functionsWithGaps}</span>
            <span className="cisa-dashboard-stat-label">Functions with gaps</span>
          </div>
        )}

        <div className="cisa-dashboard-stat">
          <Package size={16} />
          <span className="cisa-dashboard-stat-value">{totalProductsAdopted}</span>
          <span className="cisa-dashboard-stat-label">Products tracked</span>
        </div>

        {configIncompleteCount > 0 && (
          <div className="cisa-dashboard-stat cisa-dashboard-stat--info">
            <TrendingUp size={16} />
            <span className="cisa-dashboard-stat-value">{configIncompleteCount}</span>
            <span className="cisa-dashboard-stat-label">Configs incomplete</span>
          </div>
        )}

        {confidenceMetrics.expiredCount > 0 && (
          <div className="cisa-dashboard-stat cisa-dashboard-stat--error">
            <AlertTriangle size={16} />
            <span className="cisa-dashboard-stat-value">{confidenceMetrics.expiredCount}</span>
            <span className="cisa-dashboard-stat-label">Evidence expired</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CisaDashboardHeader;
